import { useEffect, useRef } from 'react';

export const IDLE_TIMEOUT_MS = 60 * 60 * 1000;

// Shared through localStorage so every open tab agrees on when the user last did something:
// working in one tab keeps the others signed in, and one tab timing out signs them all out.
const LAST_ACTIVITY_KEY = 'pt360_last_activity';

const readLast = (): number => {
  try { return Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0; } catch { return 0; }
};
const writeLast = (t: number) => {
  try { localStorage.setItem(LAST_ACTIVITY_KEY, String(t)); } catch { /* storage blocked: the in-memory clock below still works */ }
};

/** Start a fresh idle clock — call on sign-in, so an old timestamp can't sign the user straight back out. */
export const resetIdleClock = () => writeLast(Date.now());
export const clearIdleClock = () => { try { localStorage.removeItem(LAST_ACTIVITY_KEY); } catch { /* ignore */ } };

// Only real input counts as activity. Background polling (alerts, live readings) deliberately doesn't,
// or the session would never end while a dashboard is open.
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel', 'scroll'];

/**
 * Calls `onIdle` once the signed-in user has done nothing for `timeoutMs` (default 60 minutes).
 *
 * It compares timestamps on a short interval and when the tab regains focus, rather than relying on one
 * long setTimeout, because browsers throttle timers in background tabs and freeze them while a laptop sleeps.
 */
export function useIdleLogout({ enabled, onIdle, timeoutMs = IDLE_TIMEOUT_MS }: {
  enabled: boolean;
  onIdle: () => void;
  timeoutMs?: number;
}) {
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  useEffect(() => {
    if (!enabled) return;

    // First run for an existing session: no timestamp yet means "now"; an old one means the user
    // closed the browser and came back after the limit, which counts as idle.
    if (!readLast()) writeLast(Date.now());

    let lastWrite = 0;
    let fired = false;

    const check = () => {
      if (fired) return;
      const last = readLast();
      if (last && Date.now() - last >= timeoutMs) {
        fired = true;
        onIdleRef.current();
      }
    };

    const onActivity = () => {
      const now = Date.now();
      if (now - lastWrite < 5000) return;   // mousemove fires constantly; once every 5s is plenty
      lastWrite = now;
      // Don't let activity revive a session that has already lapsed (e.g. the laptop slept).
      const last = readLast();
      if (last && now - last >= timeoutMs) { check(); return; }
      writeLast(now);
    };

    const onVisible = () => { if (document.visibilityState === 'visible') check(); };

    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true, capture: true }));
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);
    const timer = setInterval(check, 10000);
    check();

    return () => {
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, onActivity, { capture: true } as EventListenerOptions));
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
      clearInterval(timer);
    };
  }, [enabled, timeoutMs]);
}
